(() => {
  const ARL = window.ARLISHCO;
  if (!ARL) return;

  const desktopQuickView = window.matchMedia('(min-width: 990px)');
  const observedRoots = new WeakSet();
  const currentScript = document.currentScript;
  const quickViewSrc = currentScript?.dataset.quickViewSrc || '';
  const quickViewHistorySrc = currentScript?.dataset.qvHistorySrc || '';
  let quickViewLoadPromise = null;

  function loadScriptOnce(src, marker) {
    if (!src) return Promise.resolve();
    const existing = document.querySelector(`script[${marker}]`);
    if (existing?.dataset.loaded === 'true') return Promise.resolve();
    if (existing) {
      return new Promise((resolve, reject) => {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.setAttribute(marker, '');
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.append(script);
    });
  }

  function loadQuickView() {
    if (typeof ARL.openQuickView === 'function') return Promise.resolve();
    if (!desktopQuickView.matches || !quickViewSrc) return Promise.reject(new Error('Quick View unavailable'));
    if (quickViewLoadPromise) return quickViewLoadPromise;

    quickViewLoadPromise = Promise.all([
      loadScriptOnce(quickViewHistorySrc, 'data-arl-qv-history-module'),
      loadScriptOnce(quickViewSrc, 'data-arl-quick-view-module'),
    ]).then(() => {
      if (typeof ARL.openQuickView !== 'function') throw new Error('Quick View unavailable');
    }).catch((error) => {
      quickViewLoadPromise = null;
      throw error;
    });

    return quickViewLoadPromise;
  }

  function removeQuickViewTriggers(root = document) {
    root.querySelectorAll?.('[data-arl-qv-trigger]').forEach((trigger) => trigger.remove());
  }

  function placeQuickViewTrigger(trigger, card, media) {
    if (!trigger || !card) return;
    if (media?.parentElement === card) {
      if (media.nextElementSibling !== trigger) media.insertAdjacentElement('afterend', trigger);
      return;
    }
    if (trigger.parentElement !== card) card.append(trigger);
  }

  function enhanceProductCards(root = document) {
    const wrappers = [];
    if (root.matches?.('.product-card-wrapper, .card-wrapper')) wrappers.push(root);
    root.querySelectorAll?.('.product-card-wrapper, .card-wrapper').forEach((wrapper) => wrappers.push(wrapper));

    wrappers.forEach((wrapper) => {
      const link = wrapper.querySelector('a[href*="/products/"]');
      const handle = ARL.productHandleFromUrl(link?.href || '');
      if (!handle) return;

      const card = wrapper.querySelector('.card');
      const media = card?.querySelector(':scope > .card__inner') || wrapper.querySelector('.card__inner');
      const wishlistMount = media || card;
      if (!card || !wishlistMount) return;

      const title = (wrapper.querySelector('.card__heading')?.textContent || link?.textContent || 'Product').trim();

      if (!wrapper.querySelector('[data-arl-wishlist-toggle]')) {
        wishlistMount.append(ARL.buildWishlistButton(handle, title, 'arl-wishlist-toggle--card'));
      }

      const existing = wrapper.querySelector('[data-arl-qv-trigger]');
      if (!desktopQuickView.matches) {
        existing?.remove();
        return;
      }
      if (existing) {
        placeQuickViewTrigger(existing, card, media);
        return;
      }

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'arl-qv-trigger';
      trigger.dataset.arlQvTrigger = '';
      trigger.dataset.productHandle = handle;
      trigger.setAttribute('aria-label', `Quick view ${title}`);
      trigger.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"/><circle cx="12" cy="12" r="2.8"/></svg><span>Quick view</span>';
      trigger.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        try {
          await loadQuickView();
          if (desktopQuickView.matches) ARL.openQuickView?.(handle, title);
        } catch (_error) {
          window.location.assign(link.href);
        }
      });
      placeQuickViewTrigger(trigger, card, media);
    });

    ARL.updateWishlistInterface(root);
  }

  function scopedWatchRoots(root = document) {
    const candidates = new Set();
    const add = (node) => {
      if (node?.nodeType === Node.ELEMENT_NODE) candidates.add(node);
    };

    if (root.matches?.('#ProductGridContainer, product-recommendations, related-products')) add(root);
    root.querySelectorAll?.('#ProductGridContainer, product-recommendations, related-products').forEach(add);

    const sections = [];
    if (root.matches?.('.shopify-section')) sections.push(root);
    root.querySelectorAll?.('.shopify-section').forEach((section) => sections.push(section));
    sections.forEach((section) => {
      if (section.querySelector('.product-card-wrapper, .card-wrapper')) add(section);
    });

    return candidates;
  }

  function observeScopedRoots(root = document) {
    scopedWatchRoots(root).forEach((watchRoot) => {
      if (observedRoots.has(watchRoot)) return;
      observedRoots.add(watchRoot);
      let timer = null;
      const observer = new MutationObserver((mutations) => {
        const relevant = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.('.product-card-wrapper, .card-wrapper') || node.querySelector?.('.product-card-wrapper, .card-wrapper'))
        ));
        if (!relevant) return;
        clearTimeout(timer);
        timer = setTimeout(() => enhanceProductCards(watchRoot), 60);
      });
      observer.observe(watchRoot, { childList: true, subtree: true });
    });
  }

  function init(root = document) {
    if (!desktopQuickView.matches) removeQuickViewTriggers(root);
    enhanceProductCards(root);
    observeScopedRoots(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  desktopQuickView.addEventListener?.('change', () => {
    if (!desktopQuickView.matches) removeQuickViewTriggers();
    init();
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
